/**
 * The Edit-with-AI controller — one skill's conversation, versions and the
 * locks between them. The reference's `useSkillEditController`, rebuilt on this
 * app's own turn shape (`useChatTurn`'s transcript: text + reasoning + steps,
 * with the interrupt panel above the composer) so the editor's chat renders
 * with the same components as the Threads pane.
 *
 * **The conversation outlives the editor** (#196). Its state lives in a store
 * held by the shell (`lib/skillEditSessions.ts`), one session per skill, and a
 * turn writes only into its own skill's session. Switching tab, closing the
 * skill or opening another one no longer aborts the edit: it keeps running —
 * listed in the sidebar's Running list — and reopening the skill shows it where
 * it is. This hook binds the open skill's session to the editor.
 *
 * Three rules the reference learned the hard way, kept with their reasons:
 *
 *   - **`busy` covers the post-commit refresh.** The stream can end and the
 *     success toast fire before the freshly-committed working copy (new
 *     parameterSchema included) has been refetched — without gating on it the
 *     user could Run against the OLD schema and send stale params.
 *   - **A resume re-inits in the mode the interrupt was created under**, not
 *     whatever the dropdown shows now.
 *   - **Once a live stream owns the thread, history never reloads over it** —
 *     the backend files the transcript only at stream end, so a reload
 *     mid-conversation replaces the live rows with nothing.
 *
 * **An AutoCAD skill names its thread on every turn** (docs/proposals/workspaces.md
 * §4–5): there is no stored pointer to move, the History menu is the agent's thread
 * records, and the gate's refusals land in `threadStatus` as a panel with a next
 * step — never as a silent empty chat. A Revit skill keeps today's pointer and
 * behaviour until its migration stage.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { API_BASE_URL } from "../lib/config";
import { describeAskError } from "../lib/askError";
import { decisionStep } from "../lib/hitlCard";
import { sentAttachment, sentAttachmentsFromUrls, storedMessageText, type Attachment } from "../lib/attachments";
import { abortTurn, AskError, type AgentStep, type ContextUsage, type InterruptPayload, type ResumeDecision } from "../lib/chat";
import { regenerateSkill, type CommitSkillVersionResult } from "../lib/skillEdit";
import {
  EMPTY_SKILL_EDIT_STATE,
  type CommitNotice,
  type SkillEditSession,
  type SkillEditSessions,
  type SkillEditState,
} from "../lib/skillEditSessions";
import type { RegenThreadSummary, SkillsClient, SkillsError } from "../lib/skills";
import { getThreadMessages } from "../lib/threads";
import { journalImagePaths, journalToMessages, withImageUrls } from "../lib/skillEditJournal";
import { THREAD_READY, threadStatusFromError, type ThreadStatus } from "../lib/threadStatus";
import type { ChatMessage, ActivePlan } from "./useChatTurn";

export type { CommitNotice } from "../lib/skillEditSessions";

export interface SkillEditView {
  messages: ChatMessage[];
  /** A stream, a restore or the post-commit refresh is in flight. */
  busy: boolean;
  streaming: boolean;
  loadingHistory: boolean;
  interrupt: InterruptPayload | null;
  usage: ContextUsage | null;
  error: string | null;
  activePlan: ActivePlan | null;
  clearPlan: () => void;
  send: (text: string, attachments?: Attachment[]) => void;
  /** AutoCAD: start a cross-version build (its own conversation); the agent asks which years. */
  buildCrossVersion: (text: string) => void;
  respond: (decision: ResumeDecision) => void;
  stop: () => void;
  /** The Edit-with-AI history menu. */
  threads: RegenThreadSummary[];
  currentThreadId: string | null;
  switchThread: (threadId: string) => Promise<void>;
  resetThread: () => void;
  /** What the gate said about the current conversation; always ready for a Revit skill. */
  threadStatus: ThreadStatus;
  /** Fetch the current conversation's transcript again, after `load_failed`. */
  retryThread: () => void;
  /** Version switch — refuses while anything else is mid-flight. */
  switchVersion: (version: number) => Promise<void>;
  restoringVersion: number | null;
  /** Bumped whenever a commit or switch changed the version list. */
  versionsKey: number;
  /**
   * Refetch the version list for a change this hook did not make.
   *
   * Publishing is the case that needs it: it moves `published_version`, which the version
   * rows render as the PUBLISHED tag, but it happens in `SkillDetail` against the skills
   * client and nothing here hears about it — so the tag stayed on whichever version had it
   * before (owner, reported 2026-09-12).
   */
  refreshVersions: () => void;
  /** The last commit, for the toast; cleared by `clearCommitNotice`. */
  commitNotice: CommitNotice | null;
  clearCommitNotice: () => void;
}

let counter = 0;
function nextId(prefix: string): string {
  counter += 1;
  return `${prefix}-se-${counter}`;
}

export function useSkillEdit({
  skillId,
  skillLabel,
  client,
  organizationId,
  userId,
  projectId,
  token,
  targetSessionId,
  agentMode,
  model,
  reasoningEffort,
  onCodeUpdated,
  sessions: store,
  t,
}: {
  skillId: string;
  /** The skill's display name — what the sidebar's Running list calls a running edit. */
  skillLabel: string;
  client: SkillsClient;
  organizationId: string | null;
  userId: string | null;
  projectId: string | null;
  token: string | null;
  /** The matching connected instance, or null — the composer is blocked without one. */
  targetSessionId: string | null;
  agentMode: string;
  model: { provider: string; model: string } | null;
  /** The reasoning chip (#111), shared with the chat pane; pinned per turn like `agentMode`. */
  reasoningEffort?: string;
  /**
   * The working copy changed on disk (commit or switch) — adopt it. Only while this
   * skill is open: an edit that commits in the background leaves the editor to load
   * the new copy when it is next opened.
   */
  onCodeUpdated: (args: { version: number; openVersion?: string; versionFolders?: string[] }) => void;
  /** Every skill's edit conversation, held by the shell (#196). */
  sessions: SkillEditSessions;
  t: (key: string, vars?: Record<string, string | number>) => string;
}): SkillEditView {
  useSyncExternalStore(store.subscribe, () => store.version);
  const shown = store.peek(skillId);
  const state: SkillEditState = shown?.state ?? EMPTY_SKILL_EDIT_STATE;

  // The editor's own, not the conversation's: both happen while the skill is open.
  const [refreshingCopy, setRefreshingCopy] = useState(false);
  const [restoringVersion, setRestoringVersion] = useState<number | null>(null);

  const tokenRef = useRef(token);
  tokenRef.current = token;
  const tRef = useRef(t);
  tRef.current = t;
  // Read when a turn reports back, possibly after the editor closed or moved on.
  const onCodeUpdatedRef = useRef(onCodeUpdated);
  onCodeUpdatedRef.current = onCodeUpdated;
  /** The skill this editor shows now; null once it has closed. */
  const openSkillRef = useRef<string | null>(skillId);
  openSkillRef.current = skillId;
  useEffect(
    () => () => {
      openSkillRef.current = null;
    },
    [],
  );

  const patch = useCallback(
    (session: SkillEditSession, change: Partial<SkillEditState>) =>
      store.update(session, (s) => ({ ...s, ...change })),
    [store],
  );

  const busy = state.streaming || refreshingCopy || restoringVersion !== null;
  /** AutoCAD keys are `autocad:<uuid>@<year>`: their turns name their thread. */
  const explicitThread = skillId.startsWith("autocad:");

  /**
   * A conversation's transcript into the session. For an AutoCAD skill a failure is a
   * status with Retry; for a Revit one it stays the empty panel it has always been.
   */
  /**
   * Whether a transcript load for `threadId` no longer matches the selection. Only an AutoCAD
   * skill names its thread on the client, so only it can move on under a running load.
   */
  const stale = useCallback(
    (session: SkillEditSession, threadId: string) => explicitThread && session.state.currentThreadId !== threadId,
    [explicitThread],
  );

  const loadTranscript = useCallback(
    async (session: SkillEditSession, threadId: string) => {
      if (!tokenRef.current) return;
      patch(session, { loadingHistory: true });
      try {
        // An AutoCAD skill's conversation keeps a Work Journal in its local workspace, with the
        // tool steps and results the cloud history drops: the transcript comes from there. A
        // conversation from before the journal has none, and falls back to the cloud history.
        // A journal that cannot be read (an agent from before the route, a folder gone) is no
        // journal: the cloud history still has the conversation's text and images.
        const journal = explicitThread ? await client.regenThreadJournal(session.skillId, threadId).catch(() => []) : [];
        let messages = journal.length > 0
          ? journalToMessages(journal)
          : historyToMessages(await getThreadMessages(tokenRef.current, threadId, { apiBaseUrl: API_BASE_URL() }));
        // The images the journal kept for its steps, fetched with the token (an <img> cannot
        // send it) and shown by object URL; one that fails to load is left out, not the transcript.
        const paths = journalImagePaths(messages);
        const urls = new Map<string, string>();
        if (paths.length > 0) {
          await Promise.all(paths.map((path) =>
            client.regenThreadJournalImage(session.skillId, threadId, path)
              .then((blob) => { urls.set(path, URL.createObjectURL(blob)); })
              .catch(() => undefined)));
          messages = withImageUrls(messages, urls);
        }
        // A load that finished after the user moved on (another conversation, a new one, a
        // live turn) is dropped, with the image URLs it made; the newer choice stands.
        if (session.ownsThread || stale(session, threadId)) {
          for (const url of urls.values()) URL.revokeObjectURL(url);
          return;
        }
        patch(session, { messages, threadStatus: THREAD_READY });
      } catch (e) {
        if (stale(session, threadId)) return;
        patch(session, {
          messages: [],
          threadStatus: { kind: "load_failed", message: e instanceof Error ? e.message : String(e) },
        });
      } finally {
        if (!stale(session, threadId)) patch(session, { loadingHistory: false });
      }
    },
    [patch, client, explicitThread, stale],
  );

  const reloadThreadList = useCallback(
    async (session: SkillEditSession) => {
      try {
        const list = await client.regenThreads(session.skillId);
        // The pointer only, never the transcript — the live rows are newer. An AutoCAD skill
        // keeps what the user chose — a conversation still in the list, or a new one armed —
        // and takes the newest only when nothing is chosen: the server's `current` is just the
        // most recently used, and the next message would go there instead.
        const chosen = session.state.currentThreadId;
        const keep = explicitThread && (session.newThread || (chosen !== null && list.threads.some((t) => t.id === chosen)));
        patch(session, {
          threads: list.threads,
          ...(!keep && list.current ? { currentThreadId: list.current } : {}),
        });
      } catch {
        /* the menu keeps its last reading */
      }
    },
    [client, patch, explicitThread],
  );

  // The thread list, and the current conversation's transcript, the first time a
  // skill is opened. A skill opened again shows its session as it is — still
  // running, finished, or waiting on an approval — and only refreshes the menu.
  useEffect(() => {
    store.session(skillId).label = skillLabel;
  }, [store, skillId, skillLabel]);

  useEffect(() => {
    const session = store.session(skillId);
    if (session.loaded) {
      if (!session.state.streaming && !session.state.interrupt) void reloadThreadList(session);
      return;
    }
    session.loaded = true;
    let cancelled = false;
    if (explicitThread) {
      void (async () => {
        try {
          const list = await client.regenThreads(skillId);
          if (cancelled) return;
          patch(session, { threads: list.threads, currentThreadId: list.current, threadStatus: THREAD_READY });
          if (list.current) await loadTranscript(session, list.current);
        } catch (e) {
          // The list itself failed: say so, rather than a blank editor that looks
          // like a skill nobody has talked to.
          if (!cancelled) {
            patch(session, { threadStatus: { kind: "load_failed", message: e instanceof Error ? e.message : String(e) } });
          }
        }
      })();
      return () => {
        cancelled = true;
        if (!session.state.messages.length && !session.state.streaming) session.loaded = false;
      };
    }
    void (async () => {
      try {
        const list = await client.regenThreads(skillId);
        if (cancelled) return;
        patch(session, { threads: list.threads, currentThreadId: list.current });
        if (list.current && tokenRef.current) {
          patch(session, { loadingHistory: true });
          try {
            const rows = await getThreadMessages(tokenRef.current, list.current, { apiBaseUrl: API_BASE_URL() });
            if (cancelled || session.ownsThread) return;
            patch(session, { messages: historyToMessages(rows) });
          } finally {
            patch(session, { loadingHistory: false });
          }
        }
      } catch {
        // No history is an empty panel, not an error strip — the agent may
        // simply predate the endpoint; the composer still works.
      }
    })();
    return () => {
      // A load abandoned half way may run again on the next open. **No abort** of a
      // running edit — that is what #196 removed.
      cancelled = true;
      if (!session.state.messages.length && !session.state.streaming) session.loaded = false;
    };
  }, [store, client, skillId, patch, reloadThreadList, explicitThread, loadTranscript]);

  const run = useCallback(
    (
      session: SkillEditSession,
      params: {
        text?: string;
        resume?: ResumeDecision;
        attachments?: Attachment[];
        /** An AutoCAD cross-version build (the agent asks which years). */
        crossVersion?: boolean;
      },
    ) => {
      if (!organizationId || !userId || !token) {
        patch(session, { error: tRef.current("chat.signInFirst") });
        return;
      }
      if (!targetSessionId) return;

      // A resume continues the interrupted turn, so it keeps that turn's settings —
      // a cross-version build's included, so its approval resumes the same build.
      if (!params.resume || !session.pinned) {
        session.pinned = {
          mode: agentMode,
          effort: reasoningEffort,
          ...(params.crossVersion ? { crossVersion: true } : {}),
        };
      }
      const pinned = session.pinned;

      const controller = new AbortController();

      const replyId = nextId("a");
      store.update(session, (s) => ({
        ...s,
        messages: [
          ...s.messages,
          ...(params.text !== undefined
            ? [{
                id: nextId("u"),
                role: "user" as const,
                text: params.text,
                // The files go with the message, as the chat shows them and as a reopened one does.
                ...(params.attachments?.length ? { attachments: params.attachments.map(sentAttachment) } : {}),
              }]
            : []),
          { id: replyId, role: "assistant" as const, text: "" },
        ],
        streaming: true,
        error: null,
        interrupt: null,
      }));
      store.setController(session, controller);

      // Every write goes to *this* skill's session, wherever the user is.
      const patchReply = (change: (m: ChatMessage) => ChatMessage) =>
        store.update(session, (s) => ({ ...s, messages: s.messages.map((m) => (m.id === replyId ? change(m) : m)) }));

      let pausedForApproval = false;
      const newThread = !params.resume && session.newThread;
      session.newThread = false;
      // AutoCAD: the thread this turn continues, or null for a new one. A resume
      // continues the turn that paused, whichever thread the menu shows now.
      const namedThread = explicitThread
        ? params.resume
          ? (session.liveThreadId ?? session.state.currentThreadId)
          : newThread
            ? null
            : session.state.currentThreadId
        : undefined;

      void regenerateSkill(
        {
          skillId: session.skillId,
          organizationId,
          userId,
          projectId,
          accessToken: token,
          targetSessionId,
          agentMode: pinned.mode,
          ...(pinned.crossVersion ? { crossVersion: true } : {}),
          ...(model ? { llmProvider: model.provider, llmModel: model.model } : {}),
          ...(pinned.effort ? { reasoningEffort: pinned.effort } : {}),
          ...(params.resume
            ? { resume: params.resume }
            : {
                prompt: params.text ?? "",
                ...(newThread ? { newThread: true } : {}),
                ...(params.attachments?.length ? { attachments: params.attachments } : {}),
              }),
          ...(namedThread !== undefined ? { threadId: namedThread } : {}),
          abortSignal: controller.signal,
        },
        {
          onThreadInfo: (id) => {
            session.ownsThread = true;
            session.liveThreadId = id;
            patch(session, { currentThreadId: id, threadStatus: THREAD_READY });
          },
          onToken: (content) => patchReply((m) => ({ ...m, text: m.text + content })),
          onReasoning: (content) => patchReply((m) => ({ ...m, reasoning: (m.reasoning ?? "") + content })),
          onSteps: (steps: AgentStep[]) => patchReply((m) => ({ ...m, steps })),
          onAborted: () =>
            store.update(session, (s) => ({
              ...s,
              messages: [...s.messages, { id: nextId("s"), role: "system" as const, text: tRef.current("chat.stoppedNotice") }],
            })),
          onInterrupt: (payload) => {
            pausedForApproval = true;
            patch(session, { interrupt: payload });
          },
          onContextUsage: (usage) => patch(session, { usage }),
          // `details` carries the billing gate's reason: the editor runs turns
          // through the same credit gate as chat, so it meets the same four
          // refusals and must not flatten them either (#64).
          onStreamError: (code, message, _traceId, details) =>
            patch(session, { error: describeAskError({ code, message, details }, tRef.current) }),
          onCodeCommitted: (result: CommitSkillVersionResult) => {
            patch(session, {
              versionsKey: session.state.versionsKey + 1,
              commitNotice: { version: result.version, summary: result.summary?.trim() || null },
            });
            // The working copy on disk is the source of truth — refetch rather
            // than trusting anything captured off the stream, and hold `busy`
            // until the new schema is adopted so Run cannot race it. Only into an
            // editor that still shows this skill: a closed one loads it on open.
            if (openSkillRef.current !== session.skillId) return;
            setRefreshingCopy(true);
            void (async () => {
              try {
                onCodeUpdatedRef.current({ version: result.version });
              } finally {
                setRefreshingCopy(false);
              }
            })();
          },
        },
      )
        .catch((e: unknown) => {
          // The gate refused the conversation itself: a panel with a next step,
          // not an error line under a composer that can never succeed.
          const refused = explicitThread ? threadStatusFromError(e) : null;
          if (refused) {
            patch(session, { threadStatus: refused, error: null });
            return;
          }
          patch(session, {
            error:
              e instanceof AskError
                ? describeAskError(e, tRef.current)
                : e instanceof Error
                  ? e.message
                  : String(e),
          });
        })
        .finally(() => {
          store.update(session, (s) => ({
            ...s,
            streaming: false,
            messages: s.messages.filter((m) => m.id !== replyId || m.text.trim() !== "" || m.steps?.length),
          }));
          if (session.controller === controller) store.setController(session, null);
          // The thread list may have just grown its first entry.
          if (!pausedForApproval) void reloadThreadList(session);
        });
    },
    [store, patch, organizationId, userId, projectId, token, targetSessionId, agentMode, model, reasoningEffort, reloadThreadList, explicitThread],
  );

  const send = useCallback(
    (text: string, attachments?: Attachment[]) => {
      const session = store.session(skillId);
      const trimmed = text.trim();
      if ((!trimmed && !attachments?.length) || busy || session.state.interrupt) return;
      run(session, { text: trimmed, ...(attachments?.length ? { attachments } : {}) });
    },
    [store, skillId, busy, run],
  );

  /**
   * Start an AutoCAD cross-version build, as 2.x's Revit button did: a conversation of its
   * own, on the skill's own (matching) AutoCAD; the agent asks which years to add.
   */
  const buildCrossVersion = useCallback(
    (text: string) => {
      const session = store.session(skillId);
      if (busy || session.state.interrupt) return;
      patch(session, {
        messages: [],
        interrupt: null,
        activePlan: null,
        usage: null,
        currentThreadId: null,
        threadStatus: THREAD_READY,
        error: null,
      });
      session.ownsThread = false;
      session.liveThreadId = null;
      session.newThread = true;
      run(session, { text, crossVersion: true });
    },
    [store, skillId, busy, patch, run],
  );

  const respond = useCallback(
    (decision: ResumeDecision) => {
      const session = store.session(skillId);
      if (busy) return;
      const action = session.state.interrupt?.action_requests?.[0];
      if (decision.approved && action?.name === "submit_plan" && !decision.intent) {
        const plan =
          decision.edited_plan?.trim() ||
          (typeof action.args?.["plan"] === "string" ? (action.args["plan"] as string) : "");
        const title =
          decision.edited_title?.trim() ||
          (typeof action.args?.["title"] === "string" ? (action.args["title"] as string) : "");
        if (plan) patch(session, { activePlan: { title, plan } });
      }
      store.update(session, (s) => ({
        ...s,
        interrupt: null,
        messages: action ? [
          ...s.messages,
          { id: nextId("a"), role: "assistant", text: "", steps: [decisionStep(action, decision)] },
        ] : s.messages,
      }));
      run(session, { resume: decision });
    },
    [store, skillId, busy, patch, run],
  );

  /**
   * Stop the turn — the fetch **and** the agent, exactly as the chat pane does.
   *
   * Dropping the fetch alone was the old behaviour and it did not stop the run
   * (#118): the regen route only aborts on `close`, which is a dropped socket, not
   * an abandoned read. The turn kept going — still tracing in LangSmith, still
   * spending credits, still driving Revit — while the editor looked stopped.
   */
  const stop = useCallback(() => {
    const session = store.peek(skillId);
    if (!session) return;
    session.controller?.abort();
    const id = session.liveThreadId ?? session.state.currentThreadId;
    if (id && tokenRef.current) void abortTurn(id, tokenRef.current).catch(() => undefined);
  }, [store, skillId]);

  const resetThread = useCallback(() => {
    const session = store.session(skillId);
    if (busy || session.state.interrupt) return;
    patch(session, {
      messages: [],
      interrupt: null,
      activePlan: null,
      usage: null,
      currentThreadId: null,
      threadStatus: THREAD_READY,
      error: null,
      loadingHistory: false,
    });
    session.ownsThread = false;
    session.liveThreadId = null;
    session.newThread = true;
  }, [store, skillId, busy, patch]);

  const retryThread = useCallback(() => {
    const session = store.session(skillId);
    if (busy) return;
    const current = session.state.currentThreadId;
    if (current) {
      void loadTranscript(session, current);
      return;
    }
    // The list never loaded: read it again, as on first open.
    patch(session, { threadStatus: THREAD_READY });
    void reloadThreadList(session);
  }, [store, skillId, busy, loadTranscript, patch, reloadThreadList]);

  const switchThread = useCallback(
    async (threadId: string) => {
      const session = store.session(skillId);
      if (busy || session.state.interrupt || !threadId || threadId === session.state.currentThreadId) return;
      if (explicitThread) {
        // No pointer to move: the next turn names this thread, and the gate checks it.
        session.newThread = false;
        session.ownsThread = false;
        session.liveThreadId = null;
        patch(session, {
          interrupt: null,
          activePlan: null,
          usage: null,
          error: null,
          currentThreadId: threadId,
          threadStatus: THREAD_READY,
        });
        await loadTranscript(session, threadId);
        return;
      }
      try {
        await client.setCurrentRegenThread(skillId, threadId);
      } catch (e) {
        // 404 = not in this history *or* someone else's (deliberately
        // indistinguishable); 502 = could not verify right now.
        const status = (e as SkillsError | null)?.status ?? null;
        patch(session, {
          error:
            status === 404
              ? tRef.current("toast.regenThreadNotYours")
              : status === 502
                ? tRef.current("toast.regenThreadVerifyFailed")
                : e instanceof Error
                  ? e.message
                  : String(e),
        });
        return;
      }
      session.newThread = false;
      session.ownsThread = true;
      patch(session, { interrupt: null, activePlan: null, usage: null, error: null, currentThreadId: threadId });
      if (tokenRef.current) {
        patch(session, { loadingHistory: true });
        try {
          const rows = await getThreadMessages(tokenRef.current, threadId, { apiBaseUrl: API_BASE_URL() });
          patch(session, { messages: historyToMessages(rows) });
        } catch {
          patch(session, { messages: [] });
        } finally {
          patch(session, { loadingHistory: false });
        }
      }
      void reloadThreadList(session);
    },
    [store, skillId, busy, client, patch, reloadThreadList, explicitThread, loadTranscript],
  );

  const switchVersion = useCallback(
    async (version: number) => {
      const session = store.session(skillId);
      // Never mid-stream or under a pending approval — restore and the
      // eventual commit refresh would race and the editor flicker between
      // the two outcomes.
      if (busy || session.state.interrupt) return;
      setRestoringVersion(version);
      try {
        const result = await client.restore(skillId, version);
        patch(session, { versionsKey: session.state.versionsKey + 1, commitNotice: null });
        onCodeUpdatedRef.current({
          version: result.version,
          ...(result.openVersion ? { openVersion: result.openVersion } : {}),
          ...(result.versionFolders ? { versionFolders: result.versionFolders } : {}),
        });
      } catch (e) {
        patch(session, { error: e instanceof Error ? e.message : String(e) });
      } finally {
        setRestoringVersion(null);
      }
    },
    [store, skillId, busy, client, patch],
  );

  const refreshVersions = useCallback(() => {
    const session = store.session(skillId);
    patch(session, { versionsKey: session.state.versionsKey + 1 });
  }, [store, skillId, patch]);

  const clearPlan = useCallback(() => {
    const session = store.peek(skillId);
    if (session) patch(session, { activePlan: null });
  }, [store, skillId, patch]);

  const clearCommitNotice = useCallback(() => {
    const session = store.peek(skillId);
    if (session?.state.commitNotice) patch(session, { commitNotice: null });
  }, [store, skillId, patch]);

  return {
    messages: state.messages,
    busy,
    streaming: state.streaming,
    loadingHistory: state.loadingHistory,
    interrupt: state.interrupt,
    usage: state.usage,
    error: state.error,
    activePlan: state.activePlan,
    clearPlan,
    send,
    buildCrossVersion,
    respond,
    stop,
    threads: state.threads,
    currentThreadId: state.currentThreadId,
    switchThread,
    resetThread,
    threadStatus: state.threadStatus,
    retryThread,
    switchVersion,
    restoringVersion,
    versionsKey: state.versionsKey,
    refreshVersions,
    commitNotice: state.commitNotice,
    clearCommitNotice,
  };
}

/**
 * Stored history → transcript rows. Zero-content placeholders are dropped:
 * the backend persists `("", continuation)` on resume turns and `(question,
 * "")` on interrupt-only turns, and the other half of each pair carries the
 * real text — rendering both halves would show blank bubbles.
 */
export function historyToMessages(
  rows: readonly { id: number; sender: string; content: string | null; images?: string[] | null }[],
): ChatMessage[] {
  return rows.flatMap((row) => {
    // A message's files survive as URLs only, as in the Threads pane's history.
    const attachments = sentAttachmentsFromUrls(row.images);
    const text = typeof row.content === "string" ? storedMessageText(row.content, attachments) : "";
    if (!text.trim() && attachments.length === 0) return [];
    return [{
      id: `h-${row.id}`,
      role: row.sender === "user" ? ("user" as const) : ("assistant" as const),
      text,
      ...(attachments.length > 0 ? { attachments } : {}),
    }];
  });
}
